import type { InputProps } from '@base-ui/react';
import type { InputHTMLAttributes } from 'react';
import type { DateFieldState, DateSegment as DateSegmentType } from 'react-stately';

import { Input as BaseInput, Popover } from '@base-ui/react';
import { createCalendar } from '@internationalized/date';
import { useRef, useState } from 'react';
import { I18nProvider, mergeProps, useDateField, useDateSegment, useFocusWithin } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { useDateFieldState } from 'react-stately';

import { useCollisionPadding } from '~/hooks/useCollisionPadding';
import { useIntl } from '~/hooks/useIntl';
import { srOnlyStyle } from '~/styles/utilities.css';
import { clsx } from '~/styles/utils';

import { Icon } from '../../Icon/Icon';
import { controlStateTarget, controlThemeStyle, focusRingStyle } from '../form.css';
import { Calendar } from './Calendar';
import { controlStyle, fieldStyle, literalStyle, popupStyle, segmentPlaceholderStyle, segmentStyle, triggerStyle } from './DatePicker.css';
import { toDate, useCalendarDate } from './DatePicker.utils';

type Props = Omit<InputProps, 'className' | 'defaultValue' | 'onBlur' | 'onChange' | 'onValueChange' | 'placeholder' | 'value'> & {
  className?: string;
  defaultValue?: Date | null;
  minDate?: Date;
  onBlur?: () => void;
  onChange?: (date: Date | null) => void;
  value?: Date | null;
};

export const DatePicker = ({ className, defaultValue, disabled, minDate, onBlur, onChange, readOnly, value, ...delegated }: Props) => {
  const { locale } = useIntl();

  return (
    <I18nProvider locale={locale}>
      <div className={clsx(controlThemeStyle, focusRingStyle, className)}>
        <BaseInput
          {...delegated}
          disabled={disabled}
          readOnly={readOnly}
          render={(inputProps, state) => (
            <DateField
              defaultValue={defaultValue}
              inputProps={inputProps}
              isDisabled={state.disabled}
              isReadOnly={!!readOnly}
              minDate={minDate}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
            />
          )}
        />
      </div>
    </I18nProvider>
  );
};

type DateFieldProps = Pick<Props, 'defaultValue' | 'minDate' | 'onBlur' | 'onChange' | 'value'> & {
  inputProps: InputHTMLAttributes<HTMLInputElement>;
  isDisabled: boolean;
  isReadOnly: boolean;
};

const DateField = ({ defaultValue, inputProps, isDisabled, isReadOnly, minDate, onBlur, onChange, value }: DateFieldProps) => {
  const collisionPadding = useCollisionPadding();
  const { locale } = useIntl();
  const { t } = useTranslation(['common']);

  const minValue = useCalendarDate(minDate);
  const currentValue = useCalendarDate(value);
  const initialValue = useCalendarDate(defaultValue);

  const [open, setOpen] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const state = useDateFieldState({
    createCalendar,
    defaultValue: initialValue,
    granularity: 'day',
    isDisabled,
    isReadOnly,
    locale,
    minValue,
    onChange: (value) => onChange?.(toDate(value)),
    shouldForceLeadingZeros: true,
    // `undefined` leaves the field uncontrolled; `null` is a controlled empty value.
    value: value === undefined ? undefined : currentValue,
  });

  const labelledBy = inputProps['aria-labelledby'];

  const { fieldProps, isInvalid } = useDateField(
    // `Field.Label` wins when there is one; the fallback covers a picker rendered without a label, and keeps the
    // first render (before Base UI has registered the label's id) from being treated as unlabelled.
    { 'aria-label': labelledBy ? undefined : t('common:fields.datePicker.label'), 'aria-labelledby': labelledBy, isDisabled, isReadOnly },
    state,
    fieldRef,
  );

  // React Aria commits the value on the way out, so that's where the field is reconciled with what it shows.
  const reconcile = () => {
    if (state.value === null) return;

    // Emptying a segment leaves React Aria holding the last complete date, which the field no longer shows.
    const isIncomplete = state.segments.some((segment) => segment.isEditable && segment.isPlaceholder);

    if (isIncomplete) {
      state.setValue(null);
      return;
    }

    // A two-digit year is shorthand for this century, never the year 26 itself.
    if (state.value.year < 100) state.setValue(state.value.set({ year: state.value.year + 2000 }));
  };

  const { focusWithinProps } = useFocusWithin({
    onBlurWithin: () => {
      reconcile();
      onBlur?.();
    },
  });

  return (
    <Popover.Root onOpenChange={setOpen} open={open}>
      <div className={controlStyle} ref={anchorRef}>
        <div {...mergeProps(fieldProps, focusWithinProps)} aria-invalid={isInvalid || undefined} className={fieldStyle} ref={fieldRef}>
          {state.segments.map((segment, index) => (
            <DateSegment key={index} segment={segment} state={state} />
          ))}
        </div>

        <input
          {...inputProps}
          className={clsx(controlStateTarget, srOnlyStyle)}
          onFocus={() => fieldRef.current?.querySelector<HTMLElement>('[role="spinbutton"]')?.focus()}
          tabIndex={-1}
        />

        <Popover.Trigger
          aria-label={t('common:fields.datePicker.openCalendar')}
          className={triggerStyle}
          disabled={isDisabled || isReadOnly}
          render={<button type="button" />}
        >
          <Icon name="Calendar" />
        </Popover.Trigger>
      </div>

      <Popover.Portal>
        <Popover.Positioner
          align="start"
          anchor={anchorRef}
          collisionAvoidance={{ fallbackAxisSide: 'none' }}
          collisionPadding={collisionPadding}
          sideOffset={4}
        >
          {/* React Aria's own `autoFocus` fires before the popup is positioned, which scrolls the page to the top.
              Base UI moves focus after positioning, so the selected day is reached from here instead. */}
          <Popover.Popup
            className={popupStyle}
            initialFocus={() => popupRef.current?.querySelector<HTMLElement>('td button[tabindex="0"]') ?? null}
            ref={popupRef}
          >
            <Calendar
              minValue={minValue ?? undefined}
              onChange={(value) => {
                state.setValue(value);
                setOpen(false);
              }}
              value={state.value}
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};

const DateSegment = ({ segment, state }: { segment: DateSegmentType; state: DateFieldState }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  if (segment.type === 'literal') {
    return <span className={literalStyle}>{segment.text}</span>;
  }

  return (
    <div
      {...segmentProps}
      className={clsx(segmentStyle, segment.isPlaceholder && segmentPlaceholderStyle)}
      ref={ref}
      // Reserve room for the widest value so the field doesn't jump as segments fill in.
      style={{ ...segmentProps.style, minWidth: `${segment.placeholder.length}ch` }}
    >
      {segment.text}
    </div>
  );
};
